import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import OptimizedImage from "@/components/OptimizedImage";
import heroImage from "@/assets/hero-image.jpg";
import { useContentSnippets } from "@/hooks/useContentSnippets";
import { Rocket } from "lucide-react";

interface HeroProps {
  imageSrc?: string;
  imageAlt?: string;
  title?: string;
  showButtons?: boolean;
}

const Hero = ({ 
  imageSrc = heroImage, 
  imageAlt = "Drei diverse medizinische Fachkräfte im Gespräch",
  title,
  showButtons = true
}: HeroProps) => {
  const { getSnippet, loading } = useContentSnippets();

  return (
    <section className="relative h-[700px] flex items-center text-white overflow-hidden">
      {/* Background Image */}
      <OptimizedImage
        className="absolute inset-0 w-full h-full object-cover" 
        src={imageSrc}
        alt={imageAlt}
        priority={true}
      />
      
      {/* Gradient Overlay: Dark left → Transparent right */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent" />
      
      {/* Content Container - Left Aligned */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl">
          {/* Launch Badge */}
          {!title && (
            <Badge 
              variant="outline" 
              className="mb-6 px-4 py-2 bg-white/10 backdrop-blur-sm border-white/20 text-white text-sm font-medium rounded-full inline-flex items-center gap-2"
            >
              <Rocket className="w-4 h-4" />
              Launch im März 2026 – Sichern Sie sich Ihren Platz als Pilot-Partner
            </Badge>
          )}
          
          {/* Headlines */}
          {title ? (
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight [text-shadow:_2px_2px_8px_rgb(0_0_0_/_60%)]">
              {title}
            </h1>
          ) : (
            <>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight [text-shadow:_2px_2px_8px_rgb(0_0_0_/_60%)]">
                {loading
                  ? "KI-Weiterbildung für Teams in der Pflege"
                  : getSnippet(
                      "home_hero_h1",
                      "KI-Weiterbildung für Teams in der Pflege"
                    )}
              </h1>
              <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed [text-shadow:_1px_1px_4px_rgb(0_0_0_/_50%)]">
                {loading
                  ? "Menschenzentriert. Von der Führungsebene bis zur Station – Kompetenz für alle, die Pflege gestalten."
                  : getSnippet(
                      "home_hero_subtitle",
                      "Menschenzentriert. Von der Führungsebene bis zur Station – Kompetenz für alle, die Pflege gestalten."
                    )}
              </p>
            </>
          )}
          
          {/* CTAs */}
          {showButtons && (
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/modul-0">
                <Button 
                  size="lg" 
                  className="bg-primary hover:bg-primary-hover text-primary-foreground px-8 py-6 text-lg font-semibold transition-all shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30"
                >
                  Early Access sichern
                </Button>
              </Link>
              <Link to="/warum-maip">
                <Button 
                  variant="ghost"
                  size="lg"
                  className="backdrop-blur-sm bg-white/10 border border-white/20 text-white hover:bg-white/20 hover:text-white px-8 py-6 text-lg font-semibold transition-all"
                >
                  So funktioniert's
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;
